/**
 * «Документы ▾» в шапке договора — по праву `passport_view` (в документах паспорт). Пункты — с сервера
 * (`document/registry`); пока один — «Претензия на невозврат».
 *
 * Окно претензии: слева форма (ФИО, серия/номер, телефон и сумма — из договора; кем и когда выдан, код и
 * прописка — по фото паспорта, сервер запоминает их по номеру клиента), справа фото паспорта с
 * переключателем «1 стр / Прописка». Скачать .docx или PDF; в «Чатах» — «Вставить в чат»: текст и PDF в
 * поле ответа (отправляет сотрудник). В CRM и Bitrix не пишет.
 * Спека `docs/specs/2026-10-23-claim-nonreturn-design.md`.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, FileText, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { type OnOffer } from "@/components/chat/SerialPhotos";
import { Field, inputCls } from "@/components/contract/ui";
import { MediaViewer } from "@/components/MediaViewer";
import { PASSPORT_VIEW, useCan } from "@/lib/access";
import {
  type ClaimFields,
  type DocFormat,
  type RawContract,
  claimFormQuery,
  contractDocumentsQuery,
  documentForChat,
  downloadDocument,
  passportPhotoUrl,
  writeError,
} from "@/lib/contract";
import { btn } from "@/lib/panelBtn";

type Ctx = { dogovorId: number; chatId: number | null; passport: RawContract["passport"] | undefined; onOffer: OnOffer | undefined };

export function ContractDocuments(ctx: Ctx) {
  const can = useCan(PASSPORT_VIEW);
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState<string | null>(null);
  const list = useQuery({ ...contractDocumentsQuery(ctx.dogovorId), enabled: can && open });
  const box = useRef<HTMLDivElement>(null);
  // Клик мимо меню закрывает его.
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);
  if (!can) return null;
  return (
    <div ref={box} className="relative ml-auto" data-annot={`документы договора ${ctx.dogovorId}`}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-[12px] text-accent-text hover:underline">
        <FileText size={12} aria-hidden />
        Документы
        <ChevronDown size={12} aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-[60] mt-1 flex min-w-56 flex-col rounded-[var(--radius-ui)] border border-border bg-bg py-1 text-[12.5px] shadow-lg">
          {list.isPending && <div className="px-3 py-1.5 text-fg-muted">Загружаю…</div>}
          {list.isError && <div className="px-3 py-1.5 text-err">{writeError(list.error)}</div>}
          {list.data?.map((d) => (
            <button
              key={d.code}
              type="button"
              role="menuitem"
              className="px-3 py-1.5 text-left hover:bg-bg-hover"
              onClick={() => {
                setDoc(d.code);
                setOpen(false);
              }}
            >
              {d.name}
            </button>
          ))}
        </div>
      )}
      {doc === "claim_nonreturn" && <ClaimWindow {...ctx} onClose={() => setDoc(null)} />}
    </div>
  );
}

const FIELDS: { key: keyof ClaimFields; label: string; wide?: boolean }[] = [
  { key: "fio", label: "ФИО", wide: true },
  { key: "pass_series", label: "Серия" },
  { key: "pass_number", label: "Номер" },
  { key: "issued_date", label: "Дата выдачи" },
  { key: "dep_code", label: "Код подразделения" },
  { key: "issued_by", label: "Кем выдан", wide: true },
  { key: "address", label: "Адрес прописки", wide: true },
  { key: "phone", label: "Телефон" },
  { key: "amount", label: "Сумма претензии, ₽" },
];

function ClaimWindow({ dogovorId, chatId, passport, onOffer, onClose }: Ctx & { onClose: () => void }) {
  const qc = useQueryClient();
  const form = useQuery(claimFormQuery(dogovorId));
  const [fields, setFields] = useState<ClaimFields | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => close.current?.focus(), []);
  // Подставляем ответ один раз: перечитывание после выдачи не должно затирать набранное.
  useEffect(() => {
    if (form.data && !fields) setFields(form.data.fields);
  }, [form.data, fields]);

  const run = async (what: string, job: () => Promise<string>) => {
    setBusy(what);
    setErr(null);
    setDone(null);
    try {
      setDone(await job());
      void qc.invalidateQueries({ queryKey: claimFormQuery(dogovorId).queryKey });
    } catch (e) {
      setErr(writeError(e));
    } finally {
      setBusy(null);
    }
  };
  const download = (format: DocFormat) =>
    fields && void run(format, async () => `Скачана претензия ${await downloadDocument(dogovorId, "claim_nonreturn", fields, format, chatId)}`);
  const toChat = () =>
    fields &&
    onOffer &&
    void run("chat", async () => {
      const d = await documentForChat(dogovorId, "claim_nonreturn", fields, chatId);
      onOffer(d.message, [d.file]);
      onClose();
      return d.number;
    });

  const c = form.data?.company;
  return createPortal(
    <div
      className="fixed inset-0 z-[90] grid place-items-center bg-black/30 p-4"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), onClose())}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Претензия на невозврат"
        className="flex max-h-[92vh] w-full max-w-5xl flex-col gap-3 overflow-auto rounded-[var(--radius-ui)] border border-border bg-bg p-4 text-[12.5px] text-fg shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-annot={`окно претензии договора ${dogovorId}`}
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 text-[14px] font-medium">
            Претензия на невозврат · договор <span className="font-mono">{dogovorId}</span>
          </div>
          <button ref={close} type="button" className="grid size-6 place-items-center rounded text-fg-muted hover:bg-bg-hover hover:text-fg" aria-label="Закрыть" title="Закрыть (Esc)" onClick={onClose}>
            <X size={14} aria-hidden />
          </button>
        </div>
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div className="flex flex-col gap-2">
            {form.isPending && <div className="text-fg-muted">Загружаю…</div>}
            {form.isError && <div className="text-err">{writeError(form.error)}</div>}
            {c && (
              <div className="text-fg-muted">
                От: <span className="text-fg">{c.name}</span> · печать и подпись{" "}
                {c.stamp && c.sign ? <span className="text-ok">✓</span> : <span className="text-err">нет</span>}
                {form.data?.contract && <> · договор {form.data.contract}</>}
              </div>
            )}
            {form.data?.warnings.map((w) => (
              <div key={w} className="text-warn">
                {w}
              </div>
            ))}
            {fields && (
              <div className="flex flex-wrap gap-2">
                {FIELDS.map((f) => (
                  <Field key={f.key} label={f.label} wide={f.wide ?? false}>
                    <input
                      value={fields[f.key]}
                      maxLength={500}
                      inputMode={f.key === "amount" ? "numeric" : undefined}
                      onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                      className={`${inputCls} border-border`}
                    />
                  </Field>
                ))}
              </div>
            )}
            <div className="text-[11.5px] text-fg-muted">Сумма по умолчанию — оценочная стоимость вещей договора. Пустые поля выйдут линией — дописать от руки.</div>
            {err && <div className="whitespace-pre-wrap text-err">{err}</div>}
            {done && <div className="text-ok">{done}</div>}
            <div className="flex flex-wrap gap-1.5">
              {onOffer && (
                <button type="button" disabled={!fields || busy !== null} onClick={toChat} className={btn.primary} title="Текст и PDF — в поле ответа чата; отправляете вы">
                  {busy === "chat" ? "Готовлю…" : "Вставить в чат"}
                </button>
              )}
              <button type="button" disabled={!fields || busy !== null} onClick={() => download("pdf")} className={onOffer ? btn.outline : btn.primary}>
                {busy === "pdf" ? "Готовлю…" : "Скачать PDF"}
              </button>
              <button type="button" disabled={!fields || busy !== null} onClick={() => download("docx")} className={btn.quiet}>
                {busy === "docx" ? "Готовлю…" : "Скачать .docx"}
              </button>
            </div>
          </div>
          <PassportPhotos dogovorId={dogovorId} passport={passport} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

const PAGES = [
  { type: 1, label: "1 стр" },
  { type: 2, label: "Прописка" },
] as const;

/** Фото паспорта рядом с формой: переключатель страниц, клик — во весь экран. Каждый просмотр — в журнале. */
function PassportPhotos({ dogovorId, passport }: { dogovorId: number; passport: RawContract["passport"] | undefined }) {
  const latest = (type: number) => passport?.docs.filter((d) => d.type === type).sort((a, b) => b.id - a.id)[0];
  const pages = PAGES.filter((p) => latest(p.type));
  const [page, setPage] = useState<number | null>(pages[0]?.type ?? null);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [full, setFull] = useState(false);
  const docId = page === null ? undefined : latest(page)?.id;
  useEffect(() => {
    if (docId === undefined) return;
    let alive = true;
    let made: string | null = null;
    setUrl(null);
    setErr(null);
    passportPhotoUrl(dogovorId, docId)
      .then((u) => {
        made = u;
        if (alive) setUrl(u);
        else URL.revokeObjectURL(u);
      })
      .catch((e: unknown) => alive && setErr(e instanceof Error ? e.message : "Не удалось открыть фото"));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [dogovorId, docId]);

  if (!pages.length) {
    return <div className="grid min-h-48 place-items-center rounded-[var(--radius-ui)] border border-dashed border-border text-fg-muted">Фото паспорта в договоре нет</div>;
  }
  const label = PAGES.find((p) => p.type === page)?.label ?? "";
  return (
    <div className="flex min-w-0 flex-col gap-2" data-annot={`фото паспорта договора ${dogovorId}`}>
      <div className="flex gap-1.5" role="tablist">
        {pages.map((p) => (
          <button key={p.type} type="button" role="tab" aria-selected={page === p.type} onClick={() => setPage(p.type)} className={page === p.type ? btn.outline : btn.quiet}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid min-h-48 place-items-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg-hover">
        {err && <div className="p-3 text-err">{err}</div>}
        {!err && !url && <div className="p-3 text-fg-muted">Открываю…</div>}
        {url && (
          <button type="button" className="cursor-zoom-in" title="Открыть крупно" onClick={() => setFull(true)}>
            <img src={url} alt={`Паспорт · ${label}`} className="max-h-[70vh] w-full object-contain" />
          </button>
        )}
      </div>
      {full && url && <MediaViewer items={[{ kind: "photo", url, caption: `Паспорт · ${label}` }]} start={0} onClose={() => setFull(false)} />}
    </div>
  );
}
